<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  drive: {
    type: Object,
    required: true,
  },
  /** Only show pick button for these providers (e.g. omit 'google' on Drives page) */
  pickFileProviders: {
    type: Array,
    default: () => ['google', 'onedrive', 'sharepoint'],
  },
  /** When true, button label is "View files" (e.g. on Drives overview where we don't select) */
  viewerMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'testConnection',
  'reauthorize',
  'delete',
  'pickFile',
]);

const { t } = useI18n();

const providerIcon = computed(() => {
  const icons = {
    google: 'i-woot-google-drive',
    microsoft: 'i-woot-sharepoint',
    dropbox: 'i-woot-dropbox',
  };
  return icons[props.drive.provider] || 'i-lucide-hard-drive';
});

const providerName = computed(() => {
  return (
    props.drive.name ||
    t(
      `INTEGRATION_SETTINGS.DRIVES.PROVIDERS.${props.drive.provider.toUpperCase()}`
    )
  );
});

const statusColor = computed(() => {
  if (props.drive.status === 'active')
    return 'text-green-600 dark:text-green-400';
  if (props.drive.status === 'expired') return 'text-red-600 dark:text-red-400';
  return 'text-slate-600 dark:text-slate-400';
});

const credentialSource = computed(() => {
  if (props.drive.channel_email_id || props.drive.channel_microsoft_id) {
    return t('INTEGRATION_SETTINGS.DRIVES.CREDENTIAL_SOURCE.CHANNEL');
  }
  return t('INTEGRATION_SETTINGS.DRIVES.CREDENTIAL_SOURCE.CUSTOM');
});

const canPickFile = computed(
  () =>
    props.pickFileProviders.includes(props.drive.provider) &&
    props.drive.status === 'active'
);

function openPicker() {
  if (!canPickFile.value) return;
  emit('pickFile', props.drive);
}

const handleTestConnection = () => {
  emit('testConnection', props.drive.id);
};

const handleReauthorize = () => {
  emit('reauthorize', props.drive);
};

const handleDelete = () => {
  emit('delete', props.drive.id);
};
</script>

<template>
  <div
    class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
  >
    <div class="flex items-start justify-between">
      <div
        class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700 w-fit w-12 h-12 mb-4"
      >
        <Icon
          :icon="providerIcon"
          class="size-6 text-slate-700 dark:text-slate-300"
        />
      </div>
      <span
        v-if="drive.status === 'error'"
        v-tooltip="drive.last_error"
        class="text-red-600 dark:text-red-400"
      >
        <i class="i-lucide-alert-circle text-xl" />
      </span>
    </div>

    <div class="flex flex-col flex-1">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
        {{ providerName }}
      </h3>
      <p class="text-sm text-slate-600 dark:text-slate-400 mb-6 flex-grow">
        {{ credentialSource }}
      </p>
    </div>

    <div class="flex gap-2 justify-start flex-wrap">
      <Button
        v-if="canPickFile"
        slate
        outline
        icon="i-lucide-folder-open"
        trailing-icon
        @click="openPicker"
      >
        {{
          $t(
            viewerMode
              ? 'INTEGRATION_SETTINGS.DRIVES.VIEW_FILES'
              : 'INTEGRATION_SETTINGS.DRIVES.PICK_A_FILE'
          )
        }}
      </Button>
      <Button
        slate
        outline
        icon="i-lucide-check-circle"
        trailing-icon
        @click="handleTestConnection"
      >
        {{ $t('INTEGRATION_SETTINGS.DRIVES.TEST_CONNECTION.BUTTON') }}
      </Button>
      <Button
        v-if="drive.status === 'error'"
        amber
        outline
        icon="i-lucide-refresh-cw"
        trailing-icon
        @click="handleReauthorize"
      >
        {{ $t('INTEGRATION_SETTINGS.DRIVES.REAUTHORIZE.BUTTON') }}
      </Button>
      <Button ruby outline @click="handleDelete">
        {{ $t('INTEGRATION_SETTINGS.DRIVES.DELETE.BUTTON') }}
      </Button>
    </div>
  </div>
</template>
